import { STD_ECONOMIC } from './contract-std-economic';
import { STD_SUPPLY } from './contract-std-supply';
import type { StdContractTemplate } from './contract-doc';
import { formatMoney, moneyToVietnameseWords } from './format';
import type { QuoteStatus } from './quote-status';

/**
 * P08 B2 – Hợp đồng từ đơn bán (docs/phases/P08.md §9, DEC-153 mặc định).
 * Bốn mẫu tích hợp sẵn; khi tạo, ERP điền sẵn trường từ đơn / khách / công ty. Người lập sửa được từng trường
 * và từng điều khoản (khách yêu cầu), lưu ngay. Điều khoản chứa ô trộn dạng `{Tên trường}` → in ra giá trị trường
 * hiện tại, nên sửa trường (VD địa điểm giao) không phải sửa lại điều khoản.
 * Bên A = Bên mua (khách), Bên B = Bên bán (Minh Tiến).
 *
 * Mẫu chuẩn Minh Tiến (anh Tiến gửi 09/10/2026, contract-std-*.ts): `std_economic` (Bên A = Minh Tiến bán, Bên B = khách mua),
 * `std_supply` (Bên A = khách, Bên B = Minh Tiến). Lời văn giữ nguyên file Word; chỗ trống có dữ liệu là ô trộn `{Tên|chỗ trống gốc}`.
 * In bằng bố cục riêng của mẫu (contract-doc.ts), logo chìm, Times New Roman.
 */
export const CONTRACT_TYPES = ['std_economic', 'std_supply', 'sale', 'construction', 'framework', 'economic'] as const;
export type ContractType = (typeof CONTRACT_TYPES)[number];
export type StdContractType = Extract<ContractType, 'std_economic' | 'std_supply'>;

export const STD_CONTRACT_TEMPLATES: Record<StdContractType, StdContractTemplate> = { std_economic: STD_ECONOMIC, std_supply: STD_SUPPLY };
export const isStdContractType = (t: ContractType): t is StdContractType => t === 'std_economic' || t === 'std_supply';

/** Chữ cái Bên của khách / Minh Tiến theo mẫu (mẫu cũ: khách = Bên A) */
export function contractPartyLetters(t: ContractType): { buyer: 'A' | 'B'; seller: 'A' | 'B' } {
  return isStdContractType(t) ? STD_CONTRACT_TEMPLATES[t].partyLetters : { buyer: 'A', seller: 'B' };
}

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  std_economic: 'Hợp đồng kinh tế (mẫu chuẩn)',
  std_supply: 'Hợp đồng cung cấp, lắp đặt (mẫu chuẩn)',
  sale: 'Hợp đồng mua bán',
  construction: 'Hợp đồng thi công',
  framework: 'Hợp đồng nguyên tắc',
  economic: 'Hợp đồng kinh tế',
};

export const CONTRACT_TYPE_HINTS: Record<ContractType, string> = {
  std_economic: 'Mẫu chuẩn Minh Tiến: mua bán, cung cấp hàng hóa. Minh Tiến là Bên A (bán), khách là Bên B',
  std_supply: 'Mẫu chuẩn Minh Tiến: cung cấp vật tư, thiết bị và/hoặc thi công, lắp đặt. Khách là Bên A, Minh Tiến là Bên B',
  sale: 'Mua bán hàng hóa theo đơn: thép, inox, PCCC, ống, vật tư',
  construction: 'Cung cấp vật tư và thi công lắp đặt tại công trình, có nghiệm thu, bảo hành',
  framework: 'Khung mua bán dài hạn; mỗi lần đặt hàng theo đơn riêng, đơn này làm phụ lục đơn giá',
  economic: 'Hợp đồng kinh tế theo Luật Thương mại, dùng khi khách yêu cầu tên gọi này',
};

/** Tiêu đề in đầu hợp đồng */
export const CONTRACT_TITLES: Record<ContractType, string> = {
  std_economic: 'HỢP ĐỒNG KINH TẾ',
  std_supply: 'HỢP ĐỒNG CUNG CẤP VẬT TƯ, THIẾT BỊ VÀ/HOẶC THI CÔNG, LẮP ĐẶT',
  sale: 'HỢP ĐỒNG MUA BÁN',
  construction: 'HỢP ĐỒNG THI CÔNG',
  framework: 'HỢP ĐỒNG NGUYÊN TẮC',
  economic: 'HỢP ĐỒNG KINH TẾ',
};

/**
 * Chốt Đơn – Soạn – Điều Phối PR5 (anh Tiến duyệt 15/10/2026, DEC-346): SO3 về → ERP tự lập hợp đồng nháp theo mẫu chuẩn.
 * Ngưỡng anh Tiến chốt 15/10/2026 20:09Z (tổng sau VAT): đơn từ 5 triệu mới tự sinh nháp; đơn trên 20 triệu mới cần ký.
 * Đổi ngưỡng: sửa 2 hằng số dưới (dùng chung API + web).
 */
export const CONTRACT_AUTO_MIN_TOTAL = 5_000_000;
export const CONTRACT_SIGN_REQUIRED_TOTAL = 20_000_000;
export const contractAutoDraft = (grandTotal: number) => grandTotal >= CONTRACT_AUTO_MIN_TOTAL;
export const contractSignRequired = (grandTotal: number) => grandTotal > CONTRACT_SIGN_REQUIRED_TOTAL;
/** Mẫu hợp đồng ERP tự lập khi có SO3 */
export const AUTO_CONTRACT_TYPE = 'std_economic' satisfies ContractType;

export const CONTRACT_STATUSES = ['draft', 'signed', 'cancelled'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];
export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  draft: 'Đang soạn',
  signed: 'Đã ký',
  cancelled: 'Đã hủy',
};
export const CONTRACT_CODE_SUFFIX = 'HD';

/** Trường sửa được trên hợp đồng. Tất cả là chữ (ngày dạng YYYY-MM-DD); để trống thì in dòng chấm. */
export const CONTRACT_FIELD_KEYS = [
  'contractNo', 'signDate', 'signPlace', 'basis',
  'buyerName', 'buyerAddress', 'buyerTaxCode', 'buyerPhone', 'buyerEmail', 'buyerRepresentative', 'buyerPosition', 'buyerBankAccount', 'buyerBank',
  'sellerName', 'sellerAddress', 'sellerTaxCode', 'sellerPhone', 'sellerEmail', 'sellerRepresentative', 'sellerPosition', 'sellerBankAccount', 'sellerBank',
  'projectName', 'deliveryLocation', 'deliveryTime', 'deliveryTerms', 'paymentTerms', 'warrantyTerms', 'effectiveTo', 'copies',
] as const;
export type ContractFieldKey = (typeof CONTRACT_FIELD_KEYS)[number];
export type ContractFields = Record<ContractFieldKey, string>;

export const CONTRACT_FIELD_LABELS: Record<ContractFieldKey, string> = {
  contractNo: 'Số hợp đồng',
  signDate: 'Ngày ký',
  signPlace: 'Nơi ký',
  basis: 'Căn cứ pháp lý',
  buyerName: 'Tên bên mua',
  buyerAddress: 'Địa chỉ bên mua',
  buyerTaxCode: 'MST bên mua',
  buyerPhone: 'Điện thoại bên mua',
  buyerEmail: 'Email bên mua',
  buyerRepresentative: 'Đại diện bên mua',
  buyerPosition: 'Chức vụ bên mua',
  buyerBankAccount: 'Số tài khoản bên mua',
  buyerBank: 'Ngân hàng bên mua',
  sellerName: 'Tên bên bán',
  sellerAddress: 'Địa chỉ bên bán',
  sellerTaxCode: 'MST bên bán',
  sellerPhone: 'Điện thoại bên bán',
  sellerEmail: 'Email bên bán',
  sellerRepresentative: 'Đại diện bên bán',
  sellerPosition: 'Chức vụ bên bán',
  sellerBankAccount: 'Số tài khoản bên bán',
  sellerBank: 'Ngân hàng bên bán',
  projectName: 'Công trình',
  deliveryLocation: 'Địa điểm giao hàng',
  deliveryTime: 'Thời gian giao hàng',
  deliveryTerms: 'Điều kiện giao hàng',
  paymentTerms: 'Thanh toán',
  warrantyTerms: 'Bảo hành',
  effectiveTo: 'Hiệu lực đến ngày',
  copies: 'Số bản',
};

/** Nhóm trường trên màn sửa */
export const CONTRACT_FIELD_GROUPS: { title: string; keys: ContractFieldKey[] }[] = [
  { title: 'Thông tin chung', keys: ['contractNo', 'signDate', 'signPlace', 'effectiveTo', 'copies', 'basis'] },
  { title: 'Bên A – Bên mua', keys: ['buyerName', 'buyerAddress', 'buyerTaxCode', 'buyerPhone', 'buyerEmail', 'buyerRepresentative', 'buyerPosition', 'buyerBankAccount', 'buyerBank'] },
  { title: 'Bên B – Bên bán', keys: ['sellerName', 'sellerAddress', 'sellerTaxCode', 'sellerPhone', 'sellerEmail', 'sellerRepresentative', 'sellerPosition', 'sellerBankAccount', 'sellerBank'] },
  { title: 'Giao hàng, thanh toán, bảo hành', keys: ['projectName', 'deliveryLocation', 'deliveryTime', 'deliveryTerms', 'paymentTerms', 'warrantyTerms'] },
];

/** Trường nhập nhiều dòng */
export const CONTRACT_MULTILINE_FIELDS: ContractFieldKey[] = ['basis', 'buyerAddress', 'sellerAddress', 'deliveryTerms', 'paymentTerms', 'warrantyTerms'];

export interface ContractArticle {
  title: string;
  body: string;
}

/** Dòng chỉ chứa dấu này trong điều khoản → in bảng hàng hóa của đơn tại đó */
/** Báo giá lập hợp đồng được khi chưa có đơn (khách cần hợp đồng trước): chưa lên đơn, chưa đóng */
export const CONTRACT_QUOTE_STATUSES: readonly QuoteStatus[] = ['draft', 'sent', 'negotiating', 'accepted'];

export const CONTRACT_ITEMS_MARK = '[BẢNG HÀNG HÓA]';

/** Ô trộn ngoài các trường: lấy từ đơn và tổng tiền */
export const CONTRACT_EXTRA_PLACEHOLDERS = [
  'Số đơn hàng', 'Số báo giá', 'Số PO khách', 'Tổng tiền hàng', 'Tổng tiền hàng bằng chữ', 'Thuế suất GTGT', 'Tiền thuế GTGT', 'Tổng giá trị', 'Tổng giá trị bằng chữ',
  'Ngày ký bằng chữ', 'Người ký bên mua',
] as const;

export interface ContractMergeData {
  fields: ContractFields;
  /** Lập từ báo giá chưa có đơn: null → ô {Số đơn hàng} in số báo giá */
  orderCode: string | null;
  quoteCode: string;
  customerPoNo: string | null;
  netTotal: number;
  vatTotal: number;
  grandTotal: number;
  /** Thuế suất các dòng hàng (ô {Thuế suất GTGT}) */
  vatRates?: number[];
  /** Người ký bên mua (ký online) – không có thì lấy đại diện bên mua */
  signerName?: string | null;
}

const BLANK = '……………………';

/** dd/mm/yyyy từ YYYY-MM-DD; chữ khác giữ nguyên */
export function contractDate(v: string | null | undefined): string {
  if (!v) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
}

/** "ngày 07 tháng 10 năm 2026" */
export function contractDateWords(v: string | null | undefined): string {
  const m = v ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim()) : null;
  return m ? `ngày ${m[3]} tháng ${m[2]} năm ${m[1]}` : 'ngày …… tháng …… năm ……';
}

/** Giá trị thô một ô trộn: chuỗi rỗng = chưa có dữ liệu; tên không nhận ra → null */
export function contractPlaceholderRaw(name: string, d: ContractMergeData): string | null {
  const key = (Object.keys(CONTRACT_FIELD_LABELS) as ContractFieldKey[]).find((k) => CONTRACT_FIELD_LABELS[k] === name);
  if (key) {
    const v = (d.fields[key] ?? '').trim();
    return v && (key === 'signDate' || key === 'effectiveTo') ? contractDate(v) : v;
  }
  switch (name) {
    case 'Số đơn hàng': return d.orderCode || d.quoteCode;
    case 'Số báo giá': return d.quoteCode;
    case 'Số PO khách': return d.customerPoNo ?? '';
    case 'Tổng tiền hàng': return formatMoney(d.netTotal);
    case 'Tổng tiền hàng bằng chữ': return moneyToVietnameseWords(d.netTotal);
    case 'Thuế suất GTGT': return [...new Set(d.vatRates ?? [])].sort((a, b) => a - b).join('/');
    case 'Tiền thuế GTGT': return formatMoney(d.vatTotal);
    case 'Tổng giá trị': return formatMoney(d.grandTotal);
    case 'Tổng giá trị bằng chữ': return moneyToVietnameseWords(d.grandTotal);
    case 'Ngày ký bằng chữ': return d.fields.signDate?.trim() ? contractDateWords(d.fields.signDate) : '';
    case 'Người ký bên mua': return (d.signerName ?? d.fields.buyerRepresentative ?? '').trim();
    default: return null;
  }
}

/** Giá trị một ô trộn `{Tên}` (mẫu cũ): chưa có dữ liệu → dòng chấm; tên không nhận ra → null (giữ nguyên chữ) */
export function contractPlaceholderValue(name: string, d: ContractMergeData): string | null {
  const v = contractPlaceholderRaw(name, d);
  return v === null ? null : v || BLANK;
}

/** Thay các ô `{Tên trường}` trong điều khoản bằng giá trị hiện tại */
export function fillContractText(text: string, d: ContractMergeData): string {
  return text.replace(/\{([^{}\n]{1,60})\}/g, (all, name: string) => contractPlaceholderValue(name.trim(), d) ?? all);
}

const COMMON_BASIS = [
  'Căn cứ Bộ luật Dân sự số 91/2015/QH13 ngày 24/11/2015;',
  'Căn cứ Luật Thương mại số 36/2005/QH11 ngày 14/06/2005;',
];

export function defaultContractBasis(type: ContractType): string {
  if (isStdContractType(type)) return STD_CONTRACT_TEMPLATES[type].basis;
  const lines = [...COMMON_BASIS];
  if (type === 'construction') {
    lines.push('Căn cứ Luật Xây dựng số 50/2014/QH13 ngày 18/06/2014 (sửa đổi, bổ sung năm 2020);');
    lines.push('Căn cứ Nghị định 37/2015/NĐ-CP quy định chi tiết về hợp đồng xây dựng (sửa đổi bởi Nghị định 50/2021/NĐ-CP);');
  }
  lines.push('Căn cứ nhu cầu và khả năng của hai bên.');
  return lines.join('\n');
}

const QUALITY =
  'Hàng hóa mới 100%, đúng chủng loại, quy cách, tiêu chuẩn và xuất xứ như Điều 1. Bên B cung cấp chứng chỉ xuất xứ, chất lượng (CO/CQ) của nhà sản xuất khi Bên A yêu cầu.\nHàng tính theo trọng lượng (thép, ống) được nghiệm thu theo cân thực tế hoặc barem nhà sản xuất; dung sai cho phép ±3%.';
const DELIVERY =
  'Thời gian giao hàng: {Thời gian giao hàng}.\nĐịa điểm giao hàng: {Địa điểm giao hàng}.\nĐiều kiện giao hàng: {Điều kiện giao hàng}.\nBên A cử người nhận hàng, kiểm tra số lượng, quy cách tại thời điểm giao và ký biên bản giao nhận. Sau khi ký biên bản, Bên B không chịu trách nhiệm về hao hụt, mất mát hàng hóa.';
const PAYMENT =
  '{Thanh toán}\nHình thức: chuyển khoản vào tài khoản của Bên B số {Số tài khoản bên bán} tại {Ngân hàng bên bán}.\nBên B xuất hóa đơn giá trị gia tăng cho Bên A theo giá trị hàng hóa thực giao.';
const WARRANTY = '{Bảo hành}';
const DUTIES =
  'Bên A: nhận hàng đúng thời gian, địa điểm đã thỏa thuận; thanh toán đầy đủ, đúng hạn; tạo điều kiện cho phương tiện của Bên B vào giao hàng.\nBên B: giao hàng đúng chủng loại, số lượng, chất lượng và thời gian đã thỏa thuận; cung cấp hóa đơn, chứng từ hợp lệ; bảo hành theo cam kết.';
const PENALTY =
  'Bên chậm thanh toán phải trả lãi chậm trả trên số tiền chậm trả theo lãi suất nợ quá hạn trung bình trên thị trường tại thời điểm thanh toán, tương ứng với thời gian chậm trả.\nBên vi phạm nghĩa vụ hợp đồng chịu phạt vi phạm tối đa 8% giá trị phần nghĩa vụ bị vi phạm (Điều 301 Luật Thương mại 2005) và bồi thường thiệt hại thực tế phát sinh.\nTrường hợp bất khả kháng (thiên tai, dịch bệnh, quyết định của cơ quan nhà nước…), bên bị ảnh hưởng thông báo cho bên kia bằng văn bản trong vòng 03 ngày và không bị coi là vi phạm hợp đồng.';
const DISPUTE =
  'Hai bên cam kết thực hiện đúng các điều khoản của hợp đồng. Tranh chấp phát sinh được giải quyết trước hết bằng thương lượng; không thương lượng được thì đưa ra Tòa án nhân dân có thẩm quyền giải quyết. Án phí do bên thua kiện chịu.';
const GENERAL =
  'Hợp đồng có hiệu lực kể từ ngày ký đến khi hai bên hoàn thành nghĩa vụ và thanh lý hợp đồng. Mọi sửa đổi, bổ sung phải lập thành văn bản có chữ ký của hai bên.\nHợp đồng được lập thành {Số bản} bản có giá trị pháp lý như nhau, mỗi bên giữ một nửa số bản.';

/** Điều khoản mặc định theo loại hợp đồng (sửa được trên từng hợp đồng). */
export function defaultContractArticles(type: ContractType): ContractArticle[] {
  if (isStdContractType(type)) return STD_CONTRACT_TEMPLATES[type].articles.map((a) => ({ ...a }));
  switch (type) {
    case 'construction':
      return [
        {
          title: 'NỘI DUNG CÔNG VIỆC VÀ GIÁ TRỊ HỢP ĐỒNG',
          body:
            'Bên A giao, Bên B nhận cung cấp vật tư và thi công lắp đặt cho công trình {Công trình}, địa điểm {Địa điểm giao hàng}, theo khối lượng và đơn giá sau:\n' +
            `${CONTRACT_ITEMS_MARK}\n` +
            'Tổng giá trị hợp đồng (đã gồm thuế GTGT): {Tổng giá trị} đồng.\nBằng chữ: {Tổng giá trị bằng chữ}.\nGiá trị quyết toán theo khối lượng thực tế thi công được hai bên nghiệm thu.',
        },
        {
          title: 'TIẾN ĐỘ THI CÔNG',
          body: 'Thời gian thực hiện: {Thời gian giao hàng}, kể từ ngày Bên A bàn giao mặt bằng và thanh toán tạm ứng (nếu có).\nTiến độ bị kéo dài do Bên A chậm bàn giao mặt bằng, thay đổi thiết kế hoặc do bất khả kháng thì được gia hạn tương ứng.',
        },
        {
          title: 'CHẤT LƯỢNG VÀ NGHIỆM THU',
          body:
            'Vật tư, thiết bị mới 100%, đúng chủng loại, xuất xứ như Điều 1, có CO/CQ khi Bên A yêu cầu. Thi công đúng thiết kế được duyệt và tiêu chuẩn, quy chuẩn hiện hành (hệ thống PCCC theo TCVN, QCVN liên quan).\nHai bên nghiệm thu từng phần và nghiệm thu hoàn thành bằng biên bản; Bên B phối hợp với Bên A trong nghiệm thu với cơ quan có thẩm quyền (nếu có).',
        },
        { title: 'THANH TOÁN', body: PAYMENT },
        { title: 'BẢO HÀNH', body: WARRANTY },
        {
          title: 'AN TOÀN LAO ĐỘNG VÀ VỆ SINH MÔI TRƯỜNG',
          body: 'Bên B chịu trách nhiệm về an toàn lao động, phòng chống cháy nổ và vệ sinh môi trường của người lao động do Bên B quản lý trong suốt quá trình thi công.',
        },
        {
          title: 'TRÁCH NHIỆM CỦA CÁC BÊN',
          body: 'Bên A: bàn giao mặt bằng, cung cấp hồ sơ thiết kế; cử người giám sát, nghiệm thu; thanh toán đúng hạn.\nBên B: cung cấp vật tư, nhân lực, thiết bị thi công; thi công đúng thiết kế, tiến độ và chất lượng; lập hồ sơ hoàn công; bảo hành theo cam kết.',
        },
        { title: 'PHẠT VI PHẠM VÀ BẤT KHẢ KHÁNG', body: PENALTY },
        { title: 'GIẢI QUYẾT TRANH CHẤP', body: DISPUTE },
        { title: 'ĐIỀU KHOẢN CHUNG', body: GENERAL },
      ];
    case 'framework':
      return [
        {
          title: 'ĐỐI TƯỢNG HỢP ĐỒNG',
          body: 'Bên B cung cấp cho Bên A các mặt hàng sắt thép, inox, thiết bị PCCC, ống nhựa, vật tư điện nước theo nhu cầu của Bên A trong thời gian hợp đồng có hiệu lực.\nChủng loại, số lượng, đơn giá cụ thể của từng lần mua bán được xác định theo đơn đặt hàng hoặc phụ lục do hai bên xác nhận. Đơn hàng số {Số đơn hàng} là đơn hàng đầu tiên theo hợp đồng này:\n' + CONTRACT_ITEMS_MARK,
        },
        {
          title: 'ĐẶT HÀNG VÀ XÁC NHẬN',
          body: 'Bên A đặt hàng bằng văn bản, email hoặc tin nhắn Zalo của người được Bên A giao nhiệm vụ; Bên B xác nhận đơn giá, thời gian giao trong vòng 01 ngày làm việc. Đơn hàng đã xác nhận là phụ lục không tách rời của hợp đồng này.\nĐơn giá áp dụng theo báo giá Bên B gửi tại thời điểm đặt hàng, chưa gồm thuế GTGT.',
        },
        { title: 'CHẤT LƯỢNG HÀNG HÓA', body: QUALITY },
        { title: 'GIAO NHẬN HÀNG HÓA', body: DELIVERY },
        { title: 'THANH TOÁN', body: PAYMENT },
        { title: 'TRÁCH NHIỆM CỦA CÁC BÊN', body: DUTIES },
        { title: 'PHẠT VI PHẠM VÀ BẤT KHẢ KHÁNG', body: PENALTY },
        { title: 'GIẢI QUYẾT TRANH CHẤP', body: DISPUTE },
        {
          title: 'HIỆU LỰC HỢP ĐỒNG',
          body: 'Hợp đồng có hiệu lực từ ngày ký đến hết ngày {Hiệu lực đến ngày}. Hết thời hạn mà không bên nào thông báo chấm dứt bằng văn bản trước 15 ngày thì hợp đồng tự động gia hạn thêm 12 tháng.\nMọi sửa đổi, bổ sung phải lập thành văn bản có chữ ký của hai bên. Hợp đồng được lập thành {Số bản} bản có giá trị pháp lý như nhau, mỗi bên giữ một nửa số bản.',
        },
      ];
    case 'economic':
    case 'sale':
    default:
      return [
        {
          title: 'HÀNG HÓA, SỐ LƯỢNG, ĐƠN GIÁ',
          body:
            'Bên B đồng ý bán, Bên A đồng ý mua các hàng hóa sau (theo đơn hàng {Số đơn hàng}):\n' +
            `${CONTRACT_ITEMS_MARK}\n` +
            'Tổng giá trị hợp đồng (đã gồm thuế GTGT): {Tổng giá trị} đồng.\nBằng chữ: {Tổng giá trị bằng chữ}.',
        },
        { title: 'CHẤT LƯỢNG, QUY CÁCH HÀNG HÓA', body: QUALITY },
        { title: 'GIAO NHẬN HÀNG HÓA', body: DELIVERY },
        { title: 'THANH TOÁN', body: PAYMENT },
        { title: 'BẢO HÀNH', body: WARRANTY },
        { title: 'TRÁCH NHIỆM CỦA CÁC BÊN', body: DUTIES },
        { title: 'PHẠT VI PHẠM VÀ BẤT KHẢ KHÁNG', body: PENALTY },
        { title: 'GIẢI QUYẾT TRANH CHẤP', body: DISPUTE },
        { title: 'ĐIỀU KHOẢN CHUNG', body: GENERAL },
      ];
  }
}

/** Bảo hành mặc định theo loại */
export function defaultWarrantyTerms(type: ContractType): string {
  return type === 'construction'
    ? 'Bảo hành 12 tháng kể từ ngày nghiệm thu hoàn thành bàn giao. Trong thời gian bảo hành, Bên B khắc phục hư hỏng do lỗi thi công, vật tư trong vòng 48 giờ kể từ khi nhận thông báo của Bên A.'
    : 'Bảo hành theo tiêu chuẩn của nhà sản xuất. Bên B đổi hàng lỗi do nhà sản xuất trong vòng 07 ngày kể từ ngày giao, hàng còn nguyên hiện trạng.';
}

/** Một đoạn trong điều khoản: chữ hoặc bảng hàng hóa */
export type ContractBlock = { kind: 'text'; text: string } | { kind: 'items' };

/** Tách điều khoản thành các đoạn (dòng `[BẢNG HÀNG HÓA]` thành bảng), đã thay ô trộn */
export function contractArticleBlocks(body: string, d: ContractMergeData): ContractBlock[] {
  const out: ContractBlock[] = [];
  for (const line of body.split('\n')) {
    if (line.trim() === CONTRACT_ITEMS_MARK) out.push({ kind: 'items' });
    else if (line.trim()) out.push({ kind: 'text', text: fillContractText(line, d) });
  }
  return out;
}
